import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { CompanyService } from 'src/core/services/company.service';
import { LoaderService } from 'src/core/services/loader.service';
import { SharedService } from 'src/core/services/shared.service';
import { all_countries, Country } from 'src/form/components/data/countries';
import { all_provinces } from 'src/form/components/data/provinces';
import { AddressCreatePayload } from 'src/interfaces/address';
import { Company, CompanyType, CreateCompanyPayload } from 'src/interfaces/company';

@Component({
  selector: 'app-company-create',
  templateUrl: './company-create.component.html',
  styleUrls: ['./company-create.component.scss']
})
export class CompanyCreateComponent implements OnInit {

  companyTypes: CompanyType[] = [];

  selectedCompanyType: CompanyType | null = null;

  filteredCountriesForIndividual: Observable<Country[]>;
  selectedDistributor: Company | null = null;
  showDistributor: boolean = false;

  countries = all_countries;
  provinces = all_provinces;

  isLoading: boolean = false;

  companyDetailsForm = this._formBuilder.group({
    companyName: ['', Validators.required],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    phoneNumber: ['', Validators.compose([Validators.required, Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')])],
    phoneNumberAlt: ['', Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')],
    practiceNumber: ['', Validators.required],
    vatRegistrationNumber: ['', Validators.pattern(/[0-9]+/)],
    companyType: ['', Validators.required]
  });


  addressDetailsForm = this._formBuilder.group({
    country: ['South Africa', Validators.required],
    province: ['', Validators.required],
    city: [''],
    addressLine1: ['', Validators.required],
    addressLine2: ['', Validators.required],
    buildingAptNumber: [''],
    postcode: ['', Validators.compose([Validators.required, Validators.pattern(/^[0-9]{4,5}$/)])]
  });

  constructor(
    private _sharedService: SharedService,
    private _formBuilder: FormBuilder,
    private _companyService: CompanyService,
    private _router: Router,
    private _loaderService: LoaderService
  ) { }

  ngOnInit(): void {


    this
      ._loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    (async () => {
      const data = await this._sharedService.getFormData(['company_type']);
      this.companyTypes = data ? data.company_type : [];
    })();

    this.filteredCountriesForIndividual = this
      .addressDetailsForm
      .controls['country']
      .valueChanges
      .pipe(
        startWith('South Africa'),
        map(value => this._filterCountries(value))
      );

    this.companyDetailsForm
      .controls['companyType']
      .valueChanges
      .subscribe(value => {
        this.selectedCompanyType = value;
        if(this.selectedCompanyType) this.toggleDistributor(this.selectedCompanyType);
      })
  }

  toggleDistributor(companyType: CompanyType) {

    if (companyType.code === 'DIS') {
      this.showDistributor = false;
      this.companyDetailsForm
        .patchValue({
          distributor: null
        })
    } else {
      this.showDistributor = true;
    }

  }

  async save() {

    if (this.companyDetailsForm.invalid || this.addressDetailsForm.invalid) return;

    const addressPayload: AddressCreatePayload = {
      building_apt_number: this.addressDetailsForm.value.buildingAptNumber,
      address_1: this.addressDetailsForm.value.addressLine1,
      address_2: this.addressDetailsForm.value.addressLine2,
      province: this.addressDetailsForm.value.province,
      city: this.addressDetailsForm.value.city,
      postcode: this.addressDetailsForm.value.postcode,
      country: this.addressDetailsForm.value.country
    }

    const payload: CreateCompanyPayload = {
      name: this.companyDetailsForm.value.companyName,
      email: this.companyDetailsForm.value.email,
      email_alt: this.companyDetailsForm.value.emailAlt,
      phone_number: this.companyDetailsForm.value.phoneNumber,
      phone_number_alt: this.companyDetailsForm.value.phoneNumberAlt,
      practice_number: this.companyDetailsForm.value.practiceNumber,
      vat_registration_number: this.companyDetailsForm.value.vatRegistrationNumber,
      company_type_id: this.companyDetailsForm.value.companyType ? this.companyDetailsForm.value.companyType.id : undefined,
      primary_contact_id: null,
      address: addressPayload,
      distributor_id: this.selectedDistributor ? this.selectedDistributor.id : undefined
    }

    const company: Company | null = await this._companyService
      .createCompany(payload);

    if (company)
      this._router.navigate(['portal/companies/view', company.id]);
  }

  _filterCountries(value: string) {
    return this
      .countries
      .filter((country) => {
        return country
          .name
          .toLowerCase()
          .includes(
            value.toLowerCase()
          )
      })
  }

  async openSearchDistributorDialog() {
    this.selectedDistributor = await this._companyService
      .openCompanySearchDialog("Search for a distributor", "", "DIS");

    if (!this.selectedDistributor) return;

    this
      .companyDetailsForm
      .patchValue({
        distributor: this.selectedDistributor
      });

  }

  deselectDistributor() {

    this.selectedDistributor = null;

    this
      .companyDetailsForm
      .patchValue({
        distributor: null
      });
  }

}
